import { auth } from '@/lib/firebase';
import { API_CONFIG } from '@/config/api.config';

/**
 * API helper functions - Now using Firebase Auth
 * NOTE: Ces fonctions sont conservées pour compatibilité avec le code existant
 * Il est recommandé d'utiliser directement les services API (authApi, dealsApi, etc.)
 */

async function getFirebaseToken(forceRefresh = false): Promise<string> {
  const user = auth.currentUser || await new Promise<typeof auth.currentUser>((resolve) => {
    const unsub = auth.onAuthStateChanged((nextUser) => {
      unsub();
      resolve(nextUser);
    });
    setTimeout(() => {
      unsub();
      resolve(auth.currentUser);
    }, 3000);
  });
  if (!user) throw new Error("Not signed in");
  const token = await user.getIdToken(forceRefresh);
  if (!token) throw new Error("Failed to retrieve Firebase ID token");
  return token;
}

export async function postApi(path: string, payload: any) {
  const token = await getFirebaseToken();

  const res = await fetch(`${API_CONFIG.baseURL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function getApi(path: string) {
  const token = await getFirebaseToken();

  const res = await fetch(`${API_CONFIG.baseURL}${path}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

/**
 * Fonctions Stripe Checkout - Maintenant via API NestJS
 * Utilise financeApi.createCheckoutSession() du nouveau backend
 */
export async function startOneTimeCheckout(p: {
  planId: string;
  userId: string;
  lang: "fr" | "en";
  termsVersion: string;
}) {
  const token = await getFirebaseToken();

  // Appel au backend NestJS
  const res = await fetch(`${API_CONFIG.baseURL}/payments/checkout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      type: 'one_time',
      planId: p.planId,
      userId: p.userId,
      successUrl: `${window.location.origin}/payment-success?tier=${p.planId}&session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: window.location.origin + '/payment-canceled',
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return { sessionId: data.sessionId, url: data.url };
}

export async function startSubscriptionCheckout(p: {
  planId: string;
  userId: string;
  lang: "fr" | "en";
  termsVersion: string;
}) {
  const token = await getFirebaseToken(true);
  if (!token) throw new Error("Authentication token unavailable. Please sign in again.");

  // Appel au backend NestJS
  const res = await fetch(`${API_CONFIG.baseURL}/payments/checkout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      type: 'subscription',
      planId: p.planId,
      userId: p.userId,
      successUrl: `${window.location.origin}/payment-success?tier=${p.planId}&session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: window.location.origin + '/payment-canceled',
    }),
  });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json();
  return { sessionId: data.sessionId, url: data.url };
}
